import Select from '@cartwhl/ui/atoms/Select';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import LockIcon from '@cartwhl/ui/icons/LockIcon';

import { MIN_LIMIT_RANGE_DEGREES } from '../../Manipulators/Properties/RotationLimits/constants';
import { RotationLimits, type Bone } from '../../../lib/skeleton/core';
import {
  setRotationLimitsAxisMode,
  setRotationLimitMaxValue,
  setRotationLimitMinValue,
} from '../../../lib/skeleton/rotationLimits';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import {
  AXES,
  AXIS_MODE_OPTIONS,
  INSPECTOR_AXIS_LABEL_CLASS,
  INSPECTOR_CONTROL_TEXT_STYLE,
  INSPECTOR_NUMBER_INPUT_CLASS,
  INSPECTOR_SELECT_CLASS,
  INSPECTOR_SELECT_WRAPPER_CLASS,
  ROTATION_LIMITS_TOOLTIP_TEXT,
  type AxisMode,
} from '../constants';
import {
  ensureRotationLimits,
  getAxisMode,
  pruneBonePropertiesDefaults,
} from '../utils';

interface RotationLimitsEditorProps {
  bone: Bone;
  bump: () => void;
  onHistoryAction?: (label: string, action: () => void) => void;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
}

export default function RotationLimitsEditor({
  bone,
  bump,
  onHistoryAction,
  onValueChangeStart,
  onValueChangeEnd,
}: RotationLimitsEditorProps) {
  const limits = bone.properties?.rotationLimits ?? new RotationLimits();

  const setMode = (axis: number, mode: AxisMode) => {
    const rl = ensureRotationLimits(bone);
    setRotationLimitsAxisMode(rl, axis, mode);
    pruneBonePropertiesDefaults(bone);
    bump();
  };

  const setMin = (axis: number, val: number) => {
    const rl = ensureRotationLimits(bone);
    const currentMax = rl.max[axis];
    const clamped =
      currentMax !== null
        ? Math.min(val, currentMax - MIN_LIMIT_RANGE_DEGREES)
        : val;
    setRotationLimitMinValue(rl, axis, clamped);
    if (currentMax !== null && currentMax - clamped > 360) {
      setRotationLimitMaxValue(rl, axis, clamped + 360);
    }
    pruneBonePropertiesDefaults(bone);
    bump();
  };

  const setMax = (axis: number, val: number) => {
    const rl = ensureRotationLimits(bone);
    const currentMin = rl.min[axis];
    const clamped =
      currentMin !== null
        ? Math.max(val, currentMin + MIN_LIMIT_RANGE_DEGREES)
        : val;
    setRotationLimitMaxValue(rl, axis, clamped);
    if (currentMin !== null && clamped - currentMin > 360) {
      setRotationLimitMinValue(rl, axis, clamped - 360);
    }
    pruneBonePropertiesDefaults(bone);
    bump();
  };

  return (
    <div className="space-y-2">
      <Tooltip text={ROTATION_LIMITS_TOOLTIP_TEXT} side="top" inModal>
        <span className="inline-flex">
          <Text variant="studio/body/medium/xs" className={PANEL_WRITING_CLASSNAME}>
            Rotation Limits
          </Text>
        </span>
      </Tooltip>
      {AXES.map((label, i) => {
        const mode = getAxisMode(limits, i);
        return (
          <div key={label} className="flex items-center gap-1.5">
            <Text
              as="span"
              variant="studio/body/medium/xs"
              className={INSPECTOR_AXIS_LABEL_CLASS}
            >
              {label}
            </Text>
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <div className={INSPECTOR_SELECT_WRAPPER_CLASS}>
                <Select
                  inModal
                  className={INSPECTOR_SELECT_CLASS}
                  value={mode}
                  onValueChange={(value) =>
                    onHistoryAction
                      ? onHistoryAction('Update rotation limits', () =>
                          setMode(i, value as AxisMode),
                        )
                      : setMode(i, value as AxisMode)
                  }
                  items={AXIS_MODE_OPTIONS}
                />
              </div>
              {mode === 'locked' && (
                <span className="border-gray-30/80 dark:border-gray-60/80 bg-white/65 dark:bg-gray-90/65 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-lg border">
                  <LockIcon className="text-gray-45 dark:text-gray-60 h-3.5 w-3.5" />
                </span>
              )}
              {mode === 'limited' && (
                <>
                  <input
                    type="number"
                    className={INSPECTOR_NUMBER_INPUT_CLASS}
                    style={INSPECTOR_CONTROL_TEXT_STYLE}
                    step={5}
                    value={limits.min[i] ?? ''}
                    onFocus={() => onValueChangeStart?.()}
                    onChange={(e) => setMin(i, Number(e.target.value))}
                    onBlur={() => onValueChangeEnd?.()}
                    placeholder="min"
                  />
                  <input
                    type="number"
                    className={INSPECTOR_NUMBER_INPUT_CLASS}
                    style={INSPECTOR_CONTROL_TEXT_STYLE}
                    step={5}
                    value={limits.max[i] ?? ''}
                    onFocus={() => onValueChangeStart?.()}
                    onChange={(e) => setMax(i, Number(e.target.value))}
                    onBlur={() => onValueChangeEnd?.()}
                    placeholder="max"
                  />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
